import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField, gridActionButton,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { getStatusInfo, RULE_STATUSES } from '../utils/ruleTypes';

const API_BASE = '/api/Rules';

const STATUS_ACTIONS = {
  0: [
    { action: 'mark-testing', label: 'Teste Al', color: '#0ea5e9', textColor: '#000' },
    { action: 'submit', label: 'Onaya Gönder', color: 'var(--warn)' },
  ],
  1: [{ action: 'submit', label: 'Onaya Gönder', color: 'var(--warn)' }],
  2: [
    { action: 'approve', label: 'Onayla', color: 'var(--success)' },
    { action: 'reject', label: 'Reddet', color: '#E53E3E' },
  ],
  3: [{ action: 'archive', label: 'Arşivle', color: '#64748b' }],
  4: [],
};

const STATUS_OPTIONS = Object.entries(RULE_STATUSES).map(([value, info]) => ({ value: Number(value), label: info.label }));
const statusLabel = (value) => getStatusInfo(value).label;

const COLUMN_FILTER_META = {
  priority: { type: 'number', odataField: 'Priority' },
  ruleName: { type: 'text', odataField: 'RuleName' },
  workflowName: { type: 'text', odataField: 'WorkflowName' },
  category: { type: 'text', odataField: 'Category' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['RuleName', 'WorkflowName', 'Category', 'Description'];

export function RulesPage() {
  usePageMeta('İş Kuralı Yönetimi', 'Kural kayıtlarını filtreleyin, sıralayın, test edin ve yönetin.');
  const navigate = useNavigate();
  const { canAdd, canEdit: canEditAction, canDelete: canDeleteAction } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Priority',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const canEdit = (status) => status === 0 || status === 1;
  const canDelete = (status) => status !== 3;

  const openNew = () => navigate('/rules/new');
  const openDetail = (row) => navigate(`/rules/${row.id}`, { state: { row } });
  const openEditRow = (row) => navigate(`/rules/${row.id}?edit=1`, { state: { row } });
  // Yaşam döngüsü geçişleri, versiyon geçmişi ve test artık detay sayfasında yönetiliyor.
  const goToLifecycle = (row) => navigate(`/rules/${row.id}`, { state: { row } });

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('priority', {
      header: '#', meta: { filterType: 'number' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined) ? '-' : <span className="c360-erp-chip">{v}</span>; },
    }),
    columnHelper.accessor('ruleName', {
      header: 'Kural Adı', meta: { filterType: 'text' },
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <strong>{row.original.ruleName || '-'}</strong>
          {row.original.description && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{row.original.description}</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('workflowName', { header: 'Workflow', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('category', { header: 'Kategori', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const info = getStatusInfo(row.original.status);
        const lifecycle = STATUS_ACTIONS[row.original.status] ?? [];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: info.color, fontWeight: 600, fontSize: 12 }}>● {info.label}</span>
            {lifecycle.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {lifecycle.map((item) => (
                  <button
                    key={item.action}
                    title="Yaşam döngüsü için detay sayfasını açar"
                    onClick={(e) => { e.stopPropagation(); goToLifecycle(row.original); }}
                    style={{ ...gridActionButton, background: item.color, color: item.textColor || '#fff' }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('version', {
      header: 'V.', enableColumnFilter: false,
      cell: ({ row }) => (
        <span
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
          title="Versiyon geçmişini detay sayfasında gör"
          onClick={(e) => { e.stopPropagation(); goToLifecycle(row.original); }}
        >
          v{row.original.version}
        </span>
      ),
    }),
    columnHelper.accessor('computedScore', {
      header: 'Skor', enableColumnFilter: false,
      cell: ({ row }) => (
        <span
          title={`Etki:${row.original.impactScore} Kolaylık:${row.original.easeScore} Aciliyet:${row.original.urgencyScore} İlişki:${row.original.relationScore}`}
          style={{ fontWeight: 600 }}
        >
          {(row.original.computedScore ?? 0).toFixed(1)}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEditAction && canEdit(row.original.status) && (
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>
          )}
          <button type="button" className="c360-action-btn" title="Workflow testi için detay sayfasını açar." onClick={(e) => { e.stopPropagation(); goToLifecycle(row.original); }}>Test</button>
          {canDeleteAction && canDelete(row.original.status) && (
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><DeleteIcon /></button>
          )}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEditAction, canDeleteAction]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Priority', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'priority', headerName: '#' },
        { field: 'ruleName', headerName: 'Kural Adı' },
        { field: 'description', headerName: 'Açıklama' },
        { field: 'workflowName', headerName: 'Workflow' },
        { field: 'category', headerName: 'Kategori' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
        { field: 'version', headerName: 'Versiyon' },
        { headerName: 'Skor', valueGetter: ({ row }) => (row.computedScore ?? 0).toFixed(1) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `kurallar_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Workflow / kural / kategori ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="İş Kuralı Yönetimi"
        subtitle="Kural kayıtlarını filtreleyin, sıralayın, test edin ve yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Kural</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}
